import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Use XMLHttpRequest to bypass fetch interceptors (PostHog, etc.)
class ApiClient {
  static request(url, options = {}) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const method = options.method || 'GET';
      
      xhr.open(method, url, true);
      xhr.timeout = 30000; // 30 second timeout
      
      // Set headers
      xhr.setRequestHeader('Content-Type', 'application/json');
      if (options.headers) {
        Object.entries(options.headers).forEach(([key, value]) => {
          if (key !== 'Content-Type') {
            xhr.setRequestHeader(key, value);
          }
        });
      }
      
      // Include credentials
      xhr.withCredentials = true;
      
      xhr.onload = function() {
        let data = null;
        try {
          data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        } catch (e) {
          data = { message: xhr.responseText };
        }
        
        resolve({
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          data,
          text: xhr.responseText,
        });
      };
      
      xhr.onerror = function() {
        reject(new Error('Network error. Please check your internet connection.'));
      };
      
      xhr.ontimeout = function() {
        reject(new Error('Request timed out. Please try again.'));
      };
      
      // Send request
      if (options.body) {
        xhr.send(options.body);
      } else {
        xhr.send();
      }
    });
  }

  static async post(url, body, headers = {}) {
    return this.request(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
  }

  static async get(url, headers = {}) {
    return this.request(url, {
      method: 'GET',
      headers,
    });
  }
}

// Token management utilities
const TokenManager = {
  TOKEN_KEY: 'auth_token',
  
  get() {
    try {
      return localStorage.getItem(this.TOKEN_KEY);
    } catch {
      return null;
    }
  },
  
  set(token) {
    try {
      if (token) {
        localStorage.setItem(this.TOKEN_KEY, token);
      } else {
        this.clear();
      }
    } catch (e) {
      console.warn('Failed to save token to localStorage:', e);
    }
  },
  
  clear() {
    try {
      localStorage.removeItem(this.TOKEN_KEY);
    } catch (e) {
      console.warn('Failed to clear token from localStorage:', e);
    }
  },
  
  getAuthHeaders() {
    const token = this.get();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  const checkingAuthRef = useRef(false);

  // Safe state updates (only if mounted)
  const safeSetUser = useCallback((value) => {
    if (mountedRef.current) setUser(value);
  }, []);

  const safeSetLoading = useCallback((value) => {
    if (mountedRef.current) setLoading(value);
  }, []);

  const safeSetError = useCallback((value) => {
    if (mountedRef.current) setError(value);
  }, []);

  // Check authentication status
  const checkAuth = useCallback(async () => {
    // Prevent duplicate calls
    if (checkingAuthRef.current) return;
    checkingAuthRef.current = true;

    try {
      const token = TokenManager.get();
      
      if (!token) {
        safeSetUser(null);
        safeSetLoading(false);
        checkingAuthRef.current = false;
        return;
      }

      const result = await ApiClient.get(
        `${API_URL}/api/auth/me`,
        TokenManager.getAuthHeaders()
      );

      if (result.ok && result.data) {
        safeSetUser(result.data);
      } else {
        // Invalid token, clear it
        TokenManager.clear();
        safeSetUser(null);
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      safeSetUser(null);
    } finally {
      safeSetLoading(false);
      checkingAuthRef.current = false;
    }
  }, [safeSetUser, safeSetLoading]);

  // Initialize auth on mount
  useEffect(() => {
    mountedRef.current = true;
    checkAuth();
    
    return () => {
      mountedRef.current = false;
    };
  }, [checkAuth]);

  // Login with email/password
  const login = useCallback(async (email, password) => {
    safeSetError(null);
    
    // Input validation
    if (!email || !password) {
      throw new Error('Email and password are required');
    }

    const trimmedEmail = email.trim().toLowerCase();
    
    if (!trimmedEmail.includes('@')) {
      throw new Error('Please enter a valid email address');
    }

    try {
      const result = await ApiClient.post(`${API_URL}/api/auth/login`, {
        email: trimmedEmail,
        password,
      });

      if (!result.ok) {
        const errorMessage = result.data?.detail || result.data?.message || 'Login failed';
        throw new Error(errorMessage);
      }

      if (!result.data?.token || !result.data?.user) {
        throw new Error('Invalid response from server');
      }

      // Save token and update state
      TokenManager.set(result.data.token);
      safeSetUser(result.data.user);
      
      return result.data;
    } catch (err) {
      safeSetError(err.message);
      throw err;
    }
  }, [safeSetUser, safeSetError]);

  // Register new user
  const register = useCallback(async (name, email, password, role = 'dispatcher') => {
    safeSetError(null);

    // Input validation
    if (!name || !email || !password) {
      throw new Error('Name, email, and password are required');
    }

    const trimmedEmail = email.trim().toLowerCase();
    const trimmedName = name.trim();

    if (!trimmedEmail.includes('@')) {
      throw new Error('Please enter a valid email address');
    }

    if (password.length < 6) {
      throw new Error('Password must be at least 6 characters');
    }

    try {
      const result = await ApiClient.post(`${API_URL}/api/auth/register`, {
        name: trimmedName,
        email: trimmedEmail,
        password,
        role,
      });

      if (!result.ok) {
        const errorMessage = result.data?.detail || result.data?.message || 'Registration failed';
        throw new Error(errorMessage);
      }

      if (!result.data?.token || !result.data?.user) {
        throw new Error('Invalid response from server');
      }

      // Save token and update state
      TokenManager.set(result.data.token);
      safeSetUser(result.data.user);
      
      return result.data;
    } catch (err) {
      safeSetError(err.message);
      throw err;
    }
  }, [safeSetUser, safeSetError]);

  // Google OAuth login
  const loginWithGoogle = useCallback(() => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  // Process Google OAuth callback
  const processGoogleCallback = useCallback(async (sessionId) => {
    safeSetError(null);

    if (!sessionId) {
      throw new Error('Invalid session');
    }

    try {
      const result = await ApiClient.post(`${API_URL}/api/auth/session`, {
        session_id: sessionId,
      });

      if (!result.ok) {
        throw new Error(result.data?.detail || 'Failed to process Google login');
      }

      if (result.data?.session_token) {
        TokenManager.set(result.data.session_token);
      }
      
      if (result.data?.user) {
        safeSetUser(result.data.user);
      }

      return result.data;
    } catch (err) {
      safeSetError(err.message);
      throw err;
    }
  }, [safeSetUser, safeSetError]);

  // Logout
  const logout = useCallback(async () => {
    try {
      await ApiClient.post(
        `${API_URL}/api/auth/logout`,
        {},
        TokenManager.getAuthHeaders()
      );
    } catch (err) {
      // Ignore logout errors - we're logging out anyway
      console.warn('Logout request failed:', err);
    } finally {
      TokenManager.clear();
      safeSetUser(null);
      safeSetError(null);
    }
  }, [safeSetUser, safeSetError]);

  // Get auth headers for API calls
  const getAuthHeaders = useCallback(() => {
    return TokenManager.getAuthHeaders();
  }, []);

  // Get current token
  const getToken = useCallback(() => {
    return TokenManager.get();
  }, []);

  const contextValue = {
    // State
    user,
    loading,
    error,
    isAuthenticated: !!user,
    
    // Actions
    login,
    register,
    loginWithGoogle,
    processGoogleCallback,
    logout,
    checkAuth,
    
    // Utilities
    getAuthHeaders,
    getToken,
    token: TokenManager.get(),
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
